import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { DOMAIN_OPTIONS, MID_STAT_OPTIONS } from '../../lib/userDetail';
import { initials as deriveInitials } from '../../lib/formatters';
import type { UpdateProfileRequest } from '../../api/apiSchemas';
import { Alert, Button, SelectField, TextField, type SelectOption } from '../ui';

const HANDLE_RE = /^[a-z0-9](?:[a-z0-9-]{1,38}[a-z0-9])$/;

function isHttpUrl(value: string): boolean {
  try {
    const u = new URL(value);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
}

function isIntInRange(value: string, min: number, max: number): boolean {
  const n = Number(value);
  return Number.isInteger(n) && n >= min && n <= max;
}

const profileFormSchema = z.object({
  displayName: z
    .string()
    .trim()
    .min(2, 'Tên hiển thị cần ít nhất 2 ký tự')
    .max(60, 'Tên hiển thị tối đa 60 ký tự'),
  initials: z.string().trim().max(2, 'Tối đa 2 ký tự'),
  pictureUrl: z.string().trim().refine((v) => v === '' || isHttpUrl(v), 'URL ảnh không hợp lệ'),
  title: z.string().trim().max(40, 'Tối đa 40 ký tự'),
  company: z.string().trim().max(40, 'Tối đa 40 ký tự'),
  domain: z.string(),
  tagline: z.string().trim().max(80, 'Tối đa 80 ký tự'),
  yearsExperience: z
    .string()
    .trim()
    .refine((v) => v === '' || isIntInRange(v, 0, 50), 'Nhập số năm 0–50'),
  midStatLabel: z.string(),
  midStatValue: z
    .string()
    .trim()
    .refine((v) => v === '' || isIntInRange(v, 0, 999), 'Nhập số 0–999'),
  cardHandle: z
    .string()
    .trim()
    .transform((v) => v.toLowerCase())
    .refine(
      (v) => v === '' || HANDLE_RE.test(v),
      'Chỉ gồm chữ thường, số và gạch ngang (3–40 ký tự)',
    ),
});

export type ProfileFormValues = z.infer<typeof profileFormSchema>;

export interface ProfileFormProps {
  defaultValues: ProfileFormValues;
  onSubmit: (payload: UpdateProfileRequest) => void;
  submitting: boolean;
  errorMessage?: string | null;
}

const withEmpty = (options: SelectOption[], placeholder: string): SelectOption[] => [
  { value: '', label: placeholder },
  ...options,
];

export function ProfileForm({ defaultValues, onSubmit, submitting, errorMessage }: ProfileFormProps) {
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<ProfileFormValues>({
    resolver: zodResolver(profileFormSchema),
    defaultValues,
  });

  const displayName = watch('displayName');

  function submit(values: ProfileFormValues): void {
    const payload: UpdateProfileRequest = {
      displayName: values.displayName,
      pictureUrl: values.pictureUrl,
      initials: values.initials,
      title: values.title,
      company: values.company,
      domain: values.domain,
      tagline: values.tagline,
      midStatLabel: values.midStatLabel,
    };
    if (values.yearsExperience !== '') payload.yearsExperience = Number(values.yearsExperience);
    if (values.midStatValue !== '') payload.midStatValue = Number(values.midStatValue);
    if (values.cardHandle !== '') payload.cardHandle = values.cardHandle;
    onSubmit(payload);
  }

  return (
    <form onSubmit={handleSubmit(submit)} noValidate>
      {errorMessage ? (
        <Alert tone="error" live>
          {errorMessage}
        </Alert>
      ) : null}

      <h2 className="form-section__title">Danh tính</h2>
      <TextField
        label="Họ tên"
        autoComplete="name"
        error={errors.displayName?.message}
        {...register('displayName')}
      />
      <TextField
        label="Ảnh đại diện (URL)"
        placeholder="https://…"
        hint="Dán liên kết ảnh (jpg/png/webp). Tải ảnh trực tiếp sẽ hỗ trợ sau."
        error={errors.pictureUrl?.message}
        {...register('pictureUrl')}
      />
      <TextField
        label="Initials"
        placeholder={deriveInitials(displayName)}
        hint="Tối đa 2 ký tự. Để trống sẽ tự sinh từ họ tên."
        error={errors.initials?.message}
        {...register('initials')}
      />

      <h2 className="form-section__title">Nghề nghiệp</h2>
      <TextField label="Chức danh" error={errors.title?.message} {...register('title')} />
      <TextField label="Công ty" error={errors.company?.message} {...register('company')} />
      <SelectField
        label="Lĩnh vực"
        options={withEmpty(DOMAIN_OPTIONS, '— Chọn lĩnh vực —')}
        error={errors.domain?.message}
        {...register('domain')}
      />
      <TextField
        label="Tagline / câu slogan"
        error={errors.tagline?.message}
        {...register('tagline')}
      />

      <h2 className="form-section__title">Chỉ số nổi bật</h2>
      <TextField
        label="Số năm kinh nghiệm"
        type="number"
        min={0}
        max={50}
        error={errors.yearsExperience?.message}
        {...register('yearsExperience')}
      />
      <SelectField
        label="Chỉ số chính"
        options={withEmpty(MID_STAT_OPTIONS, '— Chọn nhãn —')}
        error={errors.midStatLabel?.message}
        {...register('midStatLabel')}
      />
      <TextField
        label="Giá trị chỉ số chính"
        type="number"
        min={0}
        max={999}
        hint="Hậu tố (ví dụ dấu +) được gắn tự động theo nhãn."
        error={errors.midStatValue?.message}
        {...register('midStatValue')}
      />

      <h2 className="form-section__title">Trang công khai</h2>
      <TextField
        label="Đường dẫn công khai (handle)"
        placeholder="ten-cua-ban"
        hint="Trang thẻ của bạn: /u/handle. Chỉ chữ thường, số và gạch ngang."
        error={errors.cardHandle?.message}
        {...register('cardHandle')}
      />

      <Button type="submit" loading={submitting} disabled={submitting}>
        Lưu thay đổi
      </Button>
    </form>
  );
}
